import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import Select from "react-select";
import { TbPlus, TbUpload, TbDownload } from "react-icons/tb";
import { toast } from "react-toastify";

import Animation from "@areas/shared/components/chooseLayout/Animation";
import animationStyles from "@areas/shared/components/chooseLayout/styles/animations.module.css";

import ButtonDanger from "@areas/shared/components/buttons/ButtonDanger";
import CustomModal from "@areas/shared/components/personal/modals/CustomModal";

import { fetchWrapper } from "@helpers/fetchwrapper";

import Table from "./blocks/Table";
import {
  initialValues,
  validationSchema,
  confirm,
  ModalBodyAdd,
} from "./blocks/ModalAdd";

const dataTypes = [
  {
    value: 1,
    label: "Компании",
  },
  {
    value: 2,
    label: "ВУЗ",
  },
  {
    value: 3,
    label: "СПО",
  },
  {
    value: 4,
    label: "ШКОЛА",
  },
];

const SPECIALIST_NEEDS_TEMPLATE_FILENAME = "shablon_specialist_needs.csv";

const downloadSpecialistNeedsTemplate = () => {
  const header =
    "Наименование организации;Примечание;Отрасль;Специалист;Зарплата;Сайт;Контакты;ИНН";
  const example =
    'ООО «Пример»;;Промышленность;Инженер-программист;от 80000 до 120000;https://example.ru;+7 (999) 123-45-67, info@example.ru;0274123456';
  const content = `\uFEFF${header}\n${example}\n`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = SPECIALIST_NEEDS_TEMPLATE_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function Frame(props) {
  const [showModal, setShowModal] = useState(false);
  const [headerModal, setHeaderModal] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [dataType, setDataType] = useState(1);
  const [load, setLoad] = useState(false);
  const [company, setCompany] = useState(null);
  const [search, setSearch] = useState("");
  const [importLoad, setImportLoad] = useState(false);
  const [okvedLimit, setOkvedLimit] = useState(100);
  const [okvedLoad, setOkvedLoad] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(() => {
    setLoad(true);
    fetchWrapper
      .get(
        `/v1/api/app/allCompany/${dataType}?offset=${
          page * perPage - perPage
        }&limit=${perPage}&text=${search}`
      )
      .then((res) => {
        setData(res.data);
        setTotal(res.total);
        setLoad(false);
      });
  }, [page, perPage, dataType, search]);

  const handleSearch = useCallback((text) => {
    setSearch(text);
    setPage(1);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFillMissingOkved = () => {
    const limit = Number(okvedLimit);
    if (!Number.isFinite(limit) || limit < 0) {
      toast.error("Укажите корректный лимит (0 или больше)");
      return;
    }

    setOkvedLoad(true);
    fetchWrapper
      .post(
        `/v1/api/admin/FillMissingOkvedFromDadata?idType=${dataType}&limit=${limit}`
      )
      .then((result) => {
        const processed = result?.processed ?? result?.Processed;
        const updated = result?.updated ?? result?.Updated;
        const skipped = result?.skipped ?? result?.Skipped;
        const errors = result?.errors ?? result?.Errors ?? [];

        if (
          processed !== undefined ||
          updated !== undefined ||
          skipped !== undefined
        ) {
          toast.success(
            `ОКВЭД: обработано ${processed ?? 0}, обновлено ${updated ?? 0}, пропущено ${skipped ?? 0}`
          );
        } else {
          toast.success("Заполнение ОКВЭД из DaData завершено");
        }

        errors.slice(0, 5).forEach((error) => toast.warning(error));
        if (errors.length > 5) {
          toast.warning(`...и ещё ${errors.length - 5} ошибок`);
        }

        loadData();
      })
      .catch((error) => toast.error(error))
      .finally(() => setOkvedLoad(false));
  };

  const handleFileImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);

    setImportLoad(true);
    fetchWrapper
      .post(
        `/v1/api/admin/ImportSpecialistNeeds?companyType=${dataType}`,
        data,
        false
      )
      .then((result) => {
        const processed = result.processed ?? result.Processed ?? 0;
        const totalRows = result.totalRows ?? result.TotalRows ?? 0;
        const companiesCreated =
          result.companiesCreated ?? result.CompaniesCreated ?? 0;
        const specialistNeedsCreated =
          result.specialistNeedsCreated ?? result.SpecialistNeedsCreated ?? 0;
        const contactsAdded = result.contactsAdded ?? result.ContactsAdded ?? 0;
        const skipped = result.skipped ?? result.Skipped ?? 0;
        const errors = result.errors ?? result.Errors ?? [];

        toast.success(
          `Обработано: ${processed} из ${totalRows}. Создано компаний: ${companiesCreated}, потребностей: ${specialistNeedsCreated}, контактов: ${contactsAdded}. Пропущено: ${skipped}.`
        );

        errors.slice(0, 5).forEach((error) => toast.warning(error));
        if (errors.length > 5) {
          toast.warning(`...и ещё ${errors.length - 5} ошибок`);
        }

        loadData();
      })
      .catch((error) => toast.error(error))
      .finally(() => {
        setImportLoad(false);
        event.target.value = "";
      });
  };

  const toggle = useCallback(
    async (company) => {
      if (showModal) {
        setShowModal(false);
        setCompany(null);
      } else {
        if (!company) {
          setHeaderModal("Добавление");
        } else {
          setHeaderModal("Редактирование");
        }
        setCompany(company);
        setShowModal(true);
      }
    },
    [showModal]
  );

  return (
    <div>
      <Animation
        animationIn={animationStyles.fadeIn}
        animationOut={animationStyles.fadeOut}
      >
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <Select
                options={dataTypes}
                onChange={(e) => setDataType(e.value)}
                defaultValue={dataTypes[0]}
              />
              <div className="d-flex gap-2 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "90px" }}
                    min={0}
                    value={okvedLimit}
                    onChange={(event) => setOkvedLimit(event.target.value)}
                    disabled={okvedLoad}
                    title="Лимит записей"
                  />
                  <button
                    type="button"
                    className="btn btn-light text-nowrap"
                    onClick={handleFillMissingOkved}
                    disabled={okvedLoad}
                  >
                    {okvedLoad ? "Загрузка..." : "Заполнить ОКВЭД из DaData"}
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary text-nowrap"
                  onClick={downloadSpecialistNeedsTemplate}
                >
                  <TbDownload />
                  Скачать шаблон
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="d-none"
                  onChange={handleFileImport}
                />
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleImportClick}
                  disabled={importLoad}
                >
                  <TbUpload />
                  {importLoad
                    ? "Загрузка..."
                    : "Загрузить список специалистов"}
                </button>
                <ButtonDanger onClick={() => toggle()}>
                  <TbPlus />
                  Добавить
                </ButtonDanger>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Table
              data={data}
              load={load}
              total={total}
              handlePageChange={handlePageChange}
              handlePerRowsChange={handlePerRowsChange}
              toggleModal={toggle}
              onSearch={handleSearch}
            />
          </CardBody>
        </Card>
        <CustomModal
          isOpen={showModal}
          toggle={toggle}
          header={headerModal}
          body={(validation) => (
            <ModalBodyAdd validation={validation} onDadataSuccess={loadData} />
          )}
          validateYupSchema={validationSchema}
          inititalValues={() => initialValues(dataType, company)}
          cancel="Закрыть"
          confirm="Сохранить"
          confirmFunction={(values) => {
            confirm(values, loadData);
          }}
        />
      </Animation>
    </div>
  );
}

export default Frame;
